import { Injectable, inject } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiService } from './api.service';
import { apiBaseUrlFor, apiUrlFor } from './prism-new-routing';

/** The fields of an audited call that playback reads, on either backend. */
export interface AuditedCallAudio {
  botId?: string | null;
  audioUrl?: string | null;
  productName?: string | null;
  referer?: string | null;
}

interface MediaTicket {
  ticket: string;
  expiresAt: string;
}

/** Old API answers `{ duration }`; prism-new wraps it in `data`. */
interface DurationBody {
  duration?: number | null;
  data?: { duration?: number | null };
}

/** A ticket this close to expiry is re-minted rather than handed to the player. */
const TICKET_MARGIN_MS = 30_000;

/**
 * Playback for an audited call's recording.
 *
 * prism-new no longer returns `audioUrl` on the reports: it streams by `botId` from
 * `GET /call-audit/stream-audio/{botId}`, and since an `<audio>` element cannot send a bearer,
 * the URL carries a short-lived ticket from `POST /call-audit/media-ticket`. The old API
 * streams by the recording's raw `audioUrl`. Which one applies follows the prism-new whitelist.
 *
 * A `shareToken` (the public audit page opened from a share link) is itself the credential:
 * playback goes through `/call-audit/shared/...?token=` with no session or ticket.
 */
@Injectable({ providedIn: 'root' })
export class CallAuditAudioService {
  private readonly api = inject(ApiService);
  private readonly tickets = new Map<string, MediaTicket>();

  /** Whether `call` has what the active backend needs to stream it. */
  canPlay(call: AuditedCallAudio | null | undefined, shareToken?: string | null): boolean {
    return shareToken || this.usesPrismNew() ? !!call?.botId : !!call?.audioUrl;
  }

  /** The `<audio>` src for `call`, starting `startTime` seconds in. */
  streamUrl(call: AuditedCallAudio, startTime: number, shareToken?: string | null): Observable<string> {
    if (shareToken) {
      const base = apiUrlFor('GET', `call-audit/shared/stream-audio/${encodeURIComponent(call.botId ?? '')}`);
      return of(`${base}?${new URLSearchParams({ token: shareToken, startTime: String(startTime) })}`);
    }
    if (!this.usesPrismNew()) {
      const query = new URLSearchParams({
        url: call.audioUrl ?? '',
        productName: call.productName ?? '',
        referer: call.referer ?? '',
        startTime: String(startTime),
      });
      return of(`${environment.apiUrl}/call-audit/stream-audio?${query}`);
    }

    const botId = call.botId ?? '';
    const base = apiUrlFor('GET', `call-audit/stream-audio/${encodeURIComponent(botId)}`);
    return this.ticketFor(botId).pipe(
      map((ticket) => `${base}?${new URLSearchParams({ ticket, startTime: String(startTime) })}`),
    );
  }

  /** The recording's length in seconds, or 0 when the backend does not know it. */
  duration(call: AuditedCallAudio, shareToken?: string | null): Observable<number> {
    const botPath = encodeURIComponent(call.botId ?? '');
    const request$ = shareToken
      ? this.api.get<DurationBody>(`/call-audit/shared/audio-duration/${botPath}`, { token: shareToken })
      : this.usesPrismNew()
      ? this.api.get<DurationBody>(`/call-audit/audio-duration/${botPath}`)
      : this.api.get<DurationBody>('/call-audit/audio-duration', {
          url: call.audioUrl,
          productName: (call.productName ?? '').toLowerCase(),
          referer: call.referer ?? '',
        });
    return request$.pipe(map((res) => Number(res?.data?.duration ?? res?.duration ?? 0) || 0));
  }

  private usesPrismNew(): boolean {
    return apiBaseUrlFor('GET', 'call-audit/stream-audio/botId') !== environment.apiUrl;
  }

  /** Seeks restart the stream, so a still-valid ticket for the same recording is reused. */
  private ticketFor(botId: string): Observable<string> {
    const cached = this.tickets.get(botId);
    if (cached && Date.parse(cached.expiresAt) - Date.now() > TICKET_MARGIN_MS) {
      return of(cached.ticket);
    }
    return this.api.post<{ data?: MediaTicket }>('/call-audit/media-ticket', { botId }).pipe(
      map((res) => {
        const minted = res?.data;
        if (!minted?.ticket) throw new Error('No media ticket returned');
        this.tickets.set(botId, minted);
        return minted.ticket;
      }),
    );
  }
}
